import { useEffect, useState } from 'react';
import { I18nextProvider, useTranslation } from 'react-i18next';
import { Footer } from '@avodah-inc/connect-ui';
import { DatadogConfig } from './datadog-init';
import DatadogInit from './datadog-init';
import i18n from '@i18n/i18n.config';
import {
  getLocalStorageValue,
  hasAuthToken,
  localStorageKey,
} from '@/utils/utils';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { fetchBaseData } from '@/store/reducers/users';
import { fetchProjectDetail, fetchAllProjectThunk } from '@/store/reducers/projects';
import { useRouterParam } from '@/hooks/useParams';
import { useRouteGuard } from '@/hooks/useRouteGuard';
import Head from 'next/head';
import { initUserFlow } from '@/utils/userFlow';
import { useAuthContext } from '@/hooks/AuthProvider';

// import type { Metadata } from "next";
// export const metadata: Metadata = {
//   title: "Create Next App",
//   description: "Generated by create next app",
// };

// TODO: Fix warning in console about html inside div.
export default function RootLayout({
  children,
}: Readonly<{
  children: React.ReactNode;
}>) {
  const translation = useTranslation();
  const dispatch = useAppDispatch();
  const { projectId } = useRouterParam();
  const { profile } = useAuthContext();
  const { projectDetails } = useAppSelector(state => state.projectsReducer);
  const [datadogConfig, setDatadogConfig] = useState<DatadogConfig | null>(null);
  const [currentYear] = useState(() => new Date().getFullYear());

  // Add route guard to protect pages - handles redirect
  useRouteGuard();

  useEffect(() => {
    async function fetchDatadogConfig() {
      try {
        const response = await fetch('/config/datadog-config');
        if (!response.ok) {
          throw new Error(`Network response was not ok: ${response.statusText}`);
        }
        const data: DatadogConfig = await response.json();
        setDatadogConfig(data);
      } catch (error) {
        console.error('Failed to fetch Datadog config:', error);
        setDatadogConfig(null);
      }
    }

    // Fetch Datadog config on mount. Route guard handles redirect for protected pages.
    fetchDatadogConfig();
  }, []);

  useEffect(() => {
    async function fetchUserflowConfig() {
      try {
        const response = await fetch('/config/userflow-config');
        if (!response.ok) {
          throw new Error(`Network response was not ok: ${response.statusText}`);
        }
        const data = await response.json();
        return data;
      } catch (error) {
        console.error('Failed to fetch Userflow config:', error);
        return null;
      }
    }

    // Fetch Userflow config on mount. Route guard handles redirect for protected pages.
    fetchUserflowConfig().then((userflowConfig) => {
      if (userflowConfig?.USERFLOW_TOKEN) {
        initUserFlow(userflowConfig.USERFLOW_TOKEN);
      } else {
        console.warn('Userflow config is not available');
      }
    });
  }, []);

  useEffect(() => {
    // Load user's preferred language from localStorage on mount
    const savedLanguage = getLocalStorageValue(localStorageKey.defaultLocalLanguage, 'en');
    translation.i18n.changeLanguage(savedLanguage);
  }, [translation.i18n]);

  useEffect(() => {
    // Initial APIs - only fetch if user is actually authenticated
    const hasToken = hasAuthToken();
    if (hasToken && profile?.id) {
      dispatch(fetchBaseData());
      dispatch(fetchAllProjectThunk());
    }
  }, [profile?.id, dispatch]);

  useEffect(() => {
    if (projectId && projectDetails?.id !== projectId) {
      dispatch(fetchProjectDetail({ projectId }));
    }
  }, [projectId, dispatch, projectDetails?.id]);

  return (
    <main>
      <Head>
        <title>Woven</title>
        <link rel="shortcut icon" href="/favicon.ico" />
      </Head>
      <I18nextProvider i18n={i18n}>
        {datadogConfig && <DatadogInit config={datadogConfig} />}
        {children}
        <div id="modal-backdrop" />
        <div id="modal-container" />
        <Footer label={translation.t('footer.copyrights', { year: currentYear })} />
      </I18nextProvider>
    </main>
  );
}
